/*============================================================================
  CartUpsell
  - Lives in the cart drawer (snippets/cart-upsell.liquid)
  - Loads sections/cart-upsell.liquid through the Product Recommendations API
    for the newest product in the cart: complementary first, related as the
    fallback
  - Hides products already in the cart and shows the first [data-limit] left
  - Presents them as a momentum carousel: one card per view, wrapping around
    at both ends

  The track is not a scroll container. Its offset is a single JS-owned number
  painted through translate3d, which is what makes an eased settle possible —
  native scroll snapping resolves instantly and offers nothing to ease.
==============================================================================*/

// How far a flick carries. Higher = a quick swipe can skip more cards.
const MOMENTUM_MULTIPLIER = 220;

// Resistance when dragging past the ends. Lower = stiffer.
const RUBBER_BAND = 0.35;

// Settle duration scales with release speed: a fast flick snaps home, a slow
// drag drifts in.
const SETTLE_BASE = 480;
const SETTLE_MIN = 280;
const SETTLE_MAX = 600;

const SAMPLE_LIMIT = 6;

// Only samples this recent (ms) count towards the release velocity, so a drag
// that comes to rest before letting go does not fling
const VELOCITY_WINDOW = 100;

class CartUpsell extends HTMLElement {
  connectedCallback() {
    // Children only exist from here, not in the constructor
    if (this.initialized) return;
    this.initialized = true;

    this.viewport = this.querySelector('[data-upsell-viewport]');
    this.track = this.querySelector('[data-upsell-track]');
    this.arrows = this.querySelector('[data-upsell-arrows]');

    this.limit = parseInt(this.dataset.limit, 10) || 3;
    this.sectionId = this.dataset.sectionId || 'cart-upsell';
    this.baseUrl = this.dataset.url;

    // More than we show, so something is left once the cart is filtered out
    this.fetchLimit = 10;

    this.cartProductIds = (this.dataset.cartProductIds || '')
      .split(',')
      .filter(Boolean);

    // Product the rendered recommendations belong to
    this.loadedFor = null;

    // The real cards on show
    this.slides = [];

    // The same cards plus the wrap-around clones, in track order. Every
    // position is worked out against this list.
    this.loopSlides = [];
    this.hasClones = false;

    // Where the track sits, in px. The only source of truth for the carousel.
    this.position = 0;
    this.maxPosition = 0;
    this.activeIndex = 0;

    this.dragging = false;
    this.captured = false;
    this.samples = [];
    this.paintFrame = null;
    this.suppressClick = false;

    // Guards against a slow request overwriting a newer one
    this.requestId = 0;

    // Raw responses by URL for the life of the page. Raw, not what was shown:
    // fetchPool() still decides against the cart as it is now.
    this.cache = new Map();

    if (!this.track || !this.viewport || !this.baseUrl) return;

    document.addEventListener('ajaxProduct:added', this.onProductAdded);
    document.addEventListener('cart:updated', this.onCartUpdated);
    this.addEventListener('click', this.onClick);

    // The track survives re-renders (only its innerHTML is replaced)
    this.initDrag();

    // The drawer is display:none until it opens, so everything measures as
    // zero until then. This catches the moment it gains a size, and any
    // resize after.
    this.observer = new ResizeObserver(this.onResize);
    this.observer.observe(this.viewport);

    // The seed load waits for the page to settle rather than competing with
    // it. Safari has no requestIdleCallback.
    if (this.dataset.productId) {
      const seed = () => {
        // An add before this fired has already loaded something newer
        if (!this.requestId) this.load(this.dataset.productId);
      };

      if (window.requestIdleCallback) {
        requestIdleCallback(seed, { timeout: 3000 });
      } else {
        setTimeout(seed, 1000);
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('ajaxProduct:added', this.onProductAdded);
    document.removeEventListener('cart:updated', this.onCartUpdated);

    if (this.observer) this.observer.disconnect();

    if (this.track) {
      this.track.removeEventListener('transitionend', this.onTrackTransitionEnd);
      this.freezeIfSettling();
    }

    this.initialized = false;
  }

  /*============================================================================
    Cart events
  ==============================================================================*/
  onProductAdded = (evt) => {
    const item = this.lineItemFrom(evt.detail && evt.detail.product);
    if (!item || !item.product_id) return;

    const id = String(item.product_id);
    this.addCartProductId(id);

    if (id === this.loadedFor) {
      this.filter();
    } else {
      this.load(id);
    }
  };

  onCartUpdated = (evt) => {
    const cart = evt.detail && evt.detail.cart;
    if (!cart || !cart.items) return;

    const previousIds = this.cartProductIds;
    this.cartProductIds = cart.items.map((item) => String(item.product_id));

    if (!cart.items.length) {
      this.hide();
      return;
    }

    // The event does not say what changed. Comparing the lists is the only way
    // to tell a line leaving the cart from a quantity bump.
    const removed = previousIds.some(
      (id) => this.cartProductIds.indexOf(id) === -1
    );

    const seedInCart =
      this.loadedFor && this.cartProductIds.indexOf(this.loadedFor) !== -1;

    // Any removal re-seeds, not just the seed's own: carousel adds move the
    // seed along, so it usually survives and the suggestions would otherwise
    // sit still. Cart lines are newest first, so items[0] brings back what
    // was on show before the latest add.
    if (removed || !seedInCart) {
      this.load(cart.items[0].product_id);
    } else {
      this.filter();
    }
  };

  onClick = (evt) => {
    const arrow = evt.target.closest('[data-upsell-step]');
    if (arrow) {
      this.step(Number(arrow.dataset.upsellStep));
      return;
    }

    const button = evt.target.closest('[data-upsell-add]');
    if (!button) return;

    evt.preventDefault();
    this.addToCart(button);
  };

  onResize = () => {
    if (!this.viewport.clientWidth) return;
    if (this.dragging) return;

    // A settle in flight is finished off, not waited out: activeIndex already
    // holds its destination. Waiting on .is-settling would strand the
    // carousel — closing the drawer mid-glide fires transitioncancel, not
    // transitionend, so the class would never come off. parkOn() removes it.
    this.measure();
    this.parkOn(this.activeIndex);
  };

  /*============================================================================
    Painting — nothing else may move the track
  ==============================================================================*/
  paint() {
    this.track.style.transform = `translate3d(${-this.position}px, 0, 0)`;
  }

  measure() {
    this.maxPosition = Math.max(
      0,
      this.track.scrollWidth - this.viewport.clientWidth
    );
  }

  clampPosition(value) {
    return Math.min(Math.max(value, 0), this.maxPosition);
  }

  // Past either end the track follows only a fraction of the drag, so it reads
  // as resistance rather than a dead stop
  withRubberBand(value) {
    if (value < 0) {
      return value * RUBBER_BAND;
    }

    if (value > this.maxPosition) {
      return this.maxPosition + (value - this.maxPosition) * RUBBER_BAND;
    }

    return value;
  }

  /*============================================================================
    Dragging
  ==============================================================================*/
  initDrag() {
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);

    // Capture phase, so this runs before the delegated add-to-cart click
    this.track.addEventListener('click', this.onDragClick, true);

    this.track.addEventListener('transitionend', this.onTrackTransitionEnd);

    // Stops the browser's own image/link dragging from hijacking the gesture
    this.track.addEventListener('dragstart', (evt) => evt.preventDefault());

    this.track.addEventListener('focusin', this.onFocusIn);
  }

  // Keyboard focus on an off-screen card: the carousel moves there itself, so
  // the arrows stay in step with what is shown
  onFocusIn = (evt) => {
    // Where overflow: clip is unsupported the browser has already scrolled the
    // viewport to the focused card, without firing a scroll event
    this.viewport.scrollLeft = 0;

    const item = evt.target.closest('[data-upsell-item]');
    const loopIndex = this.loopSlides.indexOf(item);
    if (loopIndex === -1) return;

    const realIndex = this.realIndexOf(loopIndex);
    if (realIndex !== this.activeIndex) this.goToRealIndex(realIndex);
  };

  onPointerDown = (evt) => {
    if (evt.pointerType === 'mouse' && evt.button !== 0) return;

    // Grabbing the track mid-glide hands control straight back to the pointer
    this.freezeIfSettling();

    this.dragging = true;
    this.captured = false;
    this.pointerId = evt.pointerId;
    this.startX = evt.clientX;
    this.startPosition = this.position;
    this.startIndex = this.indexNearest(this.position);
    this.distance = 0;
    this.samples = [{ x: evt.clientX, t: evt.timeStamp }];

    this.track.classList.add('is-dragging');
  };

  onPointerMove = (evt) => {
    if (!this.dragging || evt.pointerId !== this.pointerId) return;

    const delta = evt.clientX - this.startX;
    this.distance = Math.abs(delta);

    // Capture keeps the drag alive once the cursor leaves the track. It waits
    // for a real drag: capturing on pointerdown retargets the following click
    // to the track, which would stop the add button working.
    if (!this.captured && this.distance > 3) {
      this.captured = true;
      this.track.setPointerCapture(evt.pointerId);
    }

    this.position = this.withRubberBand(this.startPosition - delta);

    this.samples.push({ x: evt.clientX, t: evt.timeStamp });
    if (this.samples.length > SAMPLE_LIMIT) this.samples.shift();

    // One paint per frame — pointer events can outrun the display
    if (!this.paintFrame) {
      this.paintFrame = requestAnimationFrame(() => {
        this.paintFrame = null;
        this.paint();
      });
    }
  };

  onPointerUp = (evt) => {
    if (!this.dragging) return;

    this.dragging = false;
    this.track.classList.remove('is-dragging');

    if (this.paintFrame) {
      cancelAnimationFrame(this.paintFrame);
      this.paintFrame = null;
      this.paint();
    }

    if (this.captured && this.track.hasPointerCapture(evt.pointerId)) {
      this.track.releasePointerCapture(evt.pointerId);
    }
    this.captured = false;

    // A cancel is the browser taking over for a vertical scroll, not a throw —
    // whatever sideways drift came with it must not page
    if (evt.type === 'pointercancel') {
      this.samples = [];
    } else {
      this.samples.push({ x: evt.clientX, t: evt.timeStamp });
    }

    if (this.distance > 5) {
      // The browser fires its click before this timeout, so the flag covers
      // exactly that one click and never goes stale
      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    }

    this.endDrag();
  };

  onDragClick = (evt) => {
    if (!this.suppressClick) return;

    evt.preventDefault();
    evt.stopPropagation();
    this.suppressClick = false;
  };

  /*============================================================================
    Release: velocity, projection, nearest card, eased settle
  ==============================================================================*/
  endDrag() {
    // With one card there is nowhere to page to — just unwind the rubber band
    if (this.loopSlides.length < 2) {
      this.settleTo(0, SETTLE_MIN);
      return;
    }

    const velocity = this.velocity();

    // Where the throw would carry to if it kept going
    const projected = this.clampPosition(
      this.position - velocity * MOMENTUM_MULTIPLIER
    );

    // Nearest card to the projected point, but never more than one from where
    // the drag began — on a one-card view a hard flick would otherwise sail
    // past the next product
    const index = Math.min(
      Math.max(this.indexNearest(projected), this.startIndex - 1, 0),
      this.startIndex + 1,
      this.loopSlides.length - 1
    );

    const duration = Math.min(
      SETTLE_MAX,
      Math.max(SETTLE_MIN, SETTLE_BASE - Math.abs(velocity) * 120)
    );

    this.settleTo(index, duration);
  }

  // px per ms across the recent samples, so one jittery frame at the end
  // cannot speak for the gesture
  velocity() {
    if (this.samples.length < 2) return 0;

    const last = this.samples[this.samples.length - 1];
    const recent = this.samples.filter((s) => last.t - s.t <= VELOCITY_WINDOW);
    if (recent.length < 2) return 0;

    const first = recent[0];
    const elapsed = last.t - first.t;

    if (elapsed <= 0) return 0;

    return (last.x - first.x) / elapsed;
  }

  /*============================================================================
    Animation

    The settle is a CSS transition (.is-settling), not a rAF loop, so the
    compositor runs it and a busy drawer rebuild cannot stutter the glide.
  ==============================================================================*/
  settleTo(loopIndex, duration = SETTLE_BASE) {
    const slide = this.loopSlides[loopIndex];
    if (!slide) return;

    this.freezeIfSettling();

    this.activeIndex = this.realIndexOf(loopIndex);

    // Already there (a release with no real drag, say). No transform change
    // means no transitionend, so .is-settling would never come off.
    if (Math.abs(slide.offsetLeft - this.position) < 0.5) {
      this.position = slide.offsetLeft;
      this.normalize();
      this.paint();
      return;
    }

    this.track.style.setProperty('--settle-duration', `${duration}ms`);
    this.track.classList.add('is-settling');

    this.targetLoopIndex = loopIndex;
    this.position = slide.offsetLeft;
    this.paint();
  }

  // What is actually painted. Mid-settle, this.position already holds the
  // target, not the current spot.
  currentTranslateX() {
    return new DOMMatrixReadOnly(getComputedStyle(this.track).transform).m41;
  }

  // Anything that interrupts a settle has to take over from where the track
  // really is, or it jumps to the target first and eases from there
  freezeIfSettling() {
    if (!this.track.classList.contains('is-settling')) return;

    const x = this.currentTranslateX();

    this.track.classList.remove('is-settling');
    this.position = -x;
    this.paint();

    // A transition starts from the last recalculated style. Forcing a recalc
    // here makes the next settle start from the frozen spot, not the old
    // target.
    void this.track.offsetWidth;
  }

  onTrackTransitionEnd = (evt) => {
    if (evt.target !== this.track) return;
    if (evt.propertyName !== 'transform') return;

    this.normalize();

    // Transition off before the repaint, or the invisible clone hop animates
    this.track.classList.remove('is-settling');
    this.paint();
  };

  /*============================================================================
    Positions
  ==============================================================================*/
  indexNearest(position) {
    let nearest = 0;
    let closest = Infinity;

    this.loopSlides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - position);

      if (distance < closest) {
        closest = distance;
        nearest = index;
      }
    });

    return nearest;
  }

  // Real card index -> its place in the track, shifted by the leading clone
  loopIndexOf(realIndex) {
    return this.hasClones ? realIndex + 1 : realIndex;
  }

  realIndexOf(loopIndex) {
    if (!this.hasClones) return loopIndex;

    if (loopIndex === 0) return this.slides.length - 1;
    if (loopIndex === this.loopSlides.length - 1) return 0;

    return loopIndex - 1;
  }

  goToRealIndex(realIndex) {
    this.settleTo(this.loopIndexOf(realIndex));
  }

  // One card left or right, for the arrows. Steps from where the track is
  // heading, not where it is, so quick repeated clicks each count.
  step(direction) {
    if (this.loopSlides.length < 2) return;

    const settling = this.track.classList.contains('is-settling');
    let from = settling
      ? this.targetLoopIndex
      : this.indexNearest(this.position);

    this.freezeIfSettling();

    // Heading for a clone, the next step would run off the end of the track.
    // Hop to the real card behind it first (same pixels, so unseen).
    const last = this.loopSlides.length - 1;
    if (this.hasClones && (from === 0 || from === last)) {
      const twin = this.loopIndexOf(this.realIndexOf(from));

      this.position +=
        this.loopSlides[twin].offsetLeft - this.loopSlides[from].offsetLeft;
      this.paint();

      // As in freezeIfSettling: the settle must start from the hopped spot
      void this.track.offsetWidth;

      from = twin;
    }

    this.settleTo(from + direction);
  }

  // Move without animating, for building and resizing
  parkOn(realIndex) {
    const slide = this.loopSlides[this.loopIndexOf(realIndex)];
    if (!slide) return;

    this.track.classList.remove('is-settling');

    this.position = slide.offsetLeft;
    this.paint();
  }

  // Once at rest on a clone, hop to the real card showing the same product
  normalize() {
    if (!this.hasClones) return;

    const loopIndex = this.indexNearest(this.position);

    if (loopIndex !== 0 && loopIndex !== this.loopSlides.length - 1) return;

    const real = this.realIndexOf(loopIndex);
    const twin = this.loopSlides[this.loopIndexOf(real)];

    if (twin) this.position = twin.offsetLeft;
  }

  /*============================================================================
    Loading and filtering
  ==============================================================================*/
  load(productId) {
    const requestId = ++this.requestId;

    this.fetchPool(productId)
      .then((html) => {
        if (requestId !== this.requestId) return;

        if (html) {
          this.paintPool(html, productId);
        } else {
          this.hide();
        }
      })
      .catch((e) => {
        console.error(e);
        if (requestId === this.requestId) this.hide();
      });
  }

  // Resolves with the cards' markup, or null when there is nothing to show.
  // Fetch only — addToCart() starts this alongside the add and paints later.
  //
  // Complementary products are the ones picked by hand in Search & Discovery.
  // A product with none picked comes back empty, and falls back to related.
  fetchPool(productId, intent = 'complementary') {
    const params = new URLSearchParams({
      section_id: this.sectionId,
      product_id: productId,
      limit: this.fetchLimit,
      intent
    });

    return this.fetchText(`${this.baseUrl}?${params.toString()}`)
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const results = html.querySelector('[data-upsell-results]');

        // A short hand-picked list can be entirely in the cart already
        const usable =
          results &&
          Array.from(results.querySelectorAll('[data-upsell-item]')).some(
            (item) => this.cartProductIds.indexOf(item.dataset.productId) === -1
          );

        if (!usable && intent === 'complementary') {
          return this.fetchPool(productId, 'related');
        }

        if (!results || !results.querySelector('[data-upsell-item]')) {
          return null;
        }

        return results.innerHTML;
      });
  }

  // Caches the promise, so two asks for one URL share a request. A failure is
  // dropped from the cache, so the next ask tries again.
  fetchText(url) {
    if (!this.cache.has(url)) {
      const request = fetch(url).then((response) => {
        if (!response.ok) throw new Error(`Recommendations ${response.status}`);
        return response.text();
      });

      request.catch(() => this.cache.delete(url));
      this.cache.set(url, request);
    }

    return this.cache.get(url);
  }

  paintPool(html, productId) {
    // Whatever the track was gliding towards no longer exists
    this.freezeIfSettling();

    this.track.innerHTML = html;
    this.loadedFor = String(productId);
    this.activeIndex = 0;

    this.classList.remove('is-loading');

    // The old nodes are gone. Clearing this makes filter() rebuild even when
    // the new pool happens to be the same products.
    this.slides = [];

    this.filter();
  }

  filter() {
    const visible = [];

    // Clones are rebuilt from whatever survives this pass
    this.track
      .querySelectorAll('[data-upsell-item]:not([data-upsell-clone])')
      .forEach((item) => {
        const inCart = this.cartProductIds.indexOf(item.dataset.productId) !== -1;
        const show = !inCart && visible.length < this.limit;

        item.classList.toggle('hide', !show);
        if (show) visible.push(item);
      });

    // Only rebuild on a real change. A rebuild parks on card 1, and every
    // cart:updated lands here — a quantity bump would reset the carousel.
    const changed =
      visible.map((el) => el.dataset.productId).join(',') !==
      this.slides.map((el) => el.dataset.productId).join(',');

    this.slides = visible;
    this.classList.toggle('hide', visible.length === 0);

    if (changed) {
      this.buildLoop();
      this.arrows.hidden = this.slides.length < 2;
    }
  }

  /*============================================================================
    Looping

    The track carries a copy of the last card before the first, and a copy of
    the first after the last, so there is always a card to drag towards.
    Coming to rest on a copy hops to the real card (normalize()).
  ==============================================================================*/
  buildLoop() {
    this.track
      .querySelectorAll('[data-upsell-clone]')
      .forEach((clone) => clone.remove());

    this.hasClones = this.slides.length > 1;
    this.loopSlides = this.slides.slice();

    if (this.hasClones) {
      const head = this.slides[this.slides.length - 1].cloneNode(true);
      const tail = this.slides[0].cloneNode(true);

      [head, tail].forEach((clone) => {
        clone.setAttribute('data-upsell-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        // Out of the tab order but still clickable — the click handler reads
        // the variant id straight off the copy
        clone
          .querySelectorAll('a, button')
          .forEach((el) => el.setAttribute('tabindex', '-1'));
      });

      this.track.insertBefore(head, this.slides[0]);
      this.track.appendChild(tail);

      this.loopSlides = [head].concat(this.slides, [tail]);
    }

    this.measure();

    // While the drawer is shut every offset reads 0; the resize observer
    // re-parks once it opens
    this.activeIndex = 0;
    this.parkOn(0);
  }

  hide() {
    this.classList.remove('is-loading');
    this.classList.add('hide');
  }

  // Skeleton on until paintPool() or hide(). The reflow commits the hidden
  // state first, so new cards fade in even when the class comes off in the
  // same frame.
  showLoading() {
    this.classList.add('is-loading');
    void this.offsetWidth;
  }

  /*============================================================================
    Add to cart
  ==============================================================================*/
  addToCart(button) {
    if (button.classList.contains('btn--loading')) return;

    const item = button.closest('[data-upsell-item]');
    const errorEl = item.querySelector('[data-upsell-error]');

    errorEl.textContent = '';
    button.classList.add('btn--loading');

    // Fetched alongside the add, not after, so the next recommendations are
    // often in hand when the add lands and the skeleton never shows
    const seedId = item.dataset.productId;
    const pool = this.fetchPool(seedId).catch((e) => {
      console.error(e);
      return null;
    });

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: button.dataset.variantId,
            quantity: 1,
            // Hidden from cart and checkout by the underscore. A Shopify Flow
            // turns it into an order tag that sales reports can filter on.
            properties: { _upsell: '1' }
          }
        ]
      })
    })
      .then((response) => response.json())
      .then((data) => {
        button.classList.remove('btn--loading');

        if (data.status && data.status !== 200) {
          errorEl.textContent = data.description || data.message;
          return;
        }

        const lineItem = this.lineItemFrom(data);
        const addedId =
          lineItem && lineItem.product_id ? String(lineItem.product_id) : null;

        if (addedId) this.addCartProductId(addedId);

        // Rebuild the drawer and header count
        document.dispatchEvent(new CustomEvent('cart:build'));

        // Move on to recommendations for what was just added, with the
        // skeleton covering the wait. Anything loaded after this wins.
        const requestId = ++this.requestId;
        this.showLoading();

        pool.then((html) => {
          if (requestId !== this.requestId) return;

          if (html) {
            this.paintPool(html, seedId);
          } else {
            // Nothing new: keep the pool on screen, minus the added card
            this.classList.remove('is-loading');
            this.filter();
          }
        });
      })
      .catch((e) => {
        console.error(e);
        button.classList.remove('btn--loading');
      });
  }

  /*============================================================================
    Helpers
  ==============================================================================*/
  // /cart/add.js answers with a single line item, or with { items: [...] }
  lineItemFrom(data) {
    if (!data) return null;
    if (data.items && data.items.length) return data.items[0];
    return data;
  }

  addCartProductId(id) {
    if (this.cartProductIds.indexOf(id) === -1) {
      this.cartProductIds.push(id);
    }
  }
}

customElements.define('cart-upsell', CartUpsell);
